import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../lib/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.startsWith('it');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
      toast.success(isItalian ? 'Email inviata!' : 'Email sent!');
    } catch {
      toast.error(isItalian ? 'Errore, riprova' : 'Error, try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen ept-bg-ambient flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-sm text-[#00F0FF]/70 hover:text-[#00F0FF] transition-colors"
          data-testid="back-to-login"
        >
          <ArrowLeft size={16} />
          {isItalian ? 'Torna al login' : 'Back to login'}
        </button>

        <div className="bg-[#0a0d15]/80 border border-[#00F0FF]/10 p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-heading font-bold text-white tracking-wider">
              {isItalian ? 'PASSWORD DIMENTICATA' : 'FORGOT PASSWORD'}
            </h1>
            <p className="text-sm text-slate-400">
              {isItalian
                ? 'Inserisci la tua email e ti invieremo un link per reimpostare la password'
                : 'Enter your email and we\'ll send you a reset link'}
            </p>
          </div>

          {sent ? (
            <div className="text-center space-y-4 py-4" data-testid="forgot-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#00E676]" />
              </div>
              <p className="text-white">
                {isItalian ? 'Controlla la tua casella email!' : 'Check your inbox!'}
              </p>
              <p className="text-sm text-slate-400">
                {isItalian
                  ? 'Se l\'email è registrata, riceverai un link per reimpostare la password.'
                  : 'If the email is registered, you\'ll receive a password reset link.'}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-[#00F0FF] to-[#00E676] text-black font-bold hover:opacity-90"
                data-testid="back-to-login-btn"
              >
                {isItalian ? 'TORNA AL LOGIN' : 'BACK TO LOGIN'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#00F0FF]/50" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  data-testid="forgot-email"
                  className="pl-10 h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-[#00F0FF] to-[#00E676] text-black font-bold hover:opacity-90"
                data-testid="forgot-submit"
              >
                {loading
                  ? (isItalian ? 'INVIO...' : 'SENDING...')
                  : (isItalian ? 'INVIA LINK DI RESET' : 'SEND RESET LINK')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
